/**
 * 🖨️ **이 그림이 굿즈로 인쇄될 수 있는가** — 판정은 여기 한 곳.
 *
 * ## 왜 한 곳인가
 * 같은 판정을 **세 곳**이 쓴다.
 * - `pnpm art:src` — 저장소에 인쇄용 원본이 남아 있는지 점검
 * - `pnpm art:recover` — 임시 폴더에서 되찾을 때 「쓸 만한 크기」 기준
 * - 관리자 업로드 화면 — 큰 그림을 화면용으로 줄일 때 **원본을 지킬지** 안내
 *
 * 화면마다 다시 적으면 **아무 에러 없이 갈린다**. 실제로 점검이 파일 존재만 보다가
 * 16mm 짜리를 「원본 있음」으로 통과시킨 적이 있다(2026-08-28).
 *
 * ## 왜 짧은 변인가
 * 걷기 띠는 가로로 프레임이 늘어서 있어 **전체 폭이 아니라 한 프레임**이 인쇄된다
 * (2048×512 띠 → 실제로 쓰이는 건 512px). 낱장 그림에서는 그냥 짧은 변이다.
 * 둘을 한 규칙으로 다루려면 짧은 변이 맞다.
 */

/** 굿즈 인쇄 기준 해상도. 인쇄소가 요구하는 값이다. */
export const PRINT_DPI = 300;

/**
 * 아크릴 키링 한 변(mm). 우리가 파는 것 중 **가장 작은** 품목이라 여기에 맞춘다 —
 * 여기서 되면 스티커·엽서·에코백은 더 여유가 있다.
 */
export const PRINT_MIN_MM = 50;

/** 인쇄에 쓸 수 있는 최소 픽셀 = 50mm × 300dpi ≒ 600px. */
export const PRINT_MIN_PX = Math.round((PRINT_MIN_MM / 25.4) * PRINT_DPI); // 591 → 아래에서 올림

/** 실제로 쓰는 값. 계산값(591)을 **600 으로 올려** 잡는다 — 자를 여유를 둔다. */
export const PRINT_PX = 600;

/** 이 그림에서 인쇄에 실제로 쓰이는 크기(px). 띠든 낱장이든 짧은 변이다. */
export function printableSide(width: number, height: number): number {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return 0;
  return Math.max(0, Math.min(Math.floor(width), Math.floor(height)));
}

/** 300dpi 로 찍었을 때 몇 mm 가 되나. */
export function printMm(px: number): number {
  return (px / PRINT_DPI) * 25.4;
}

/** 굿즈로 인쇄해도 뭉개지지 않는가. */
export function isPrintReady(width: number, height: number): boolean {
  return printableSide(width, height) >= PRINT_PX;
}

/**
 * 관리자에서 **큰 그림을 화면용으로 줄일 때** 원본을 지킬지 알려 주는 안내.
 *
 * 🔴 이게 없으면 원본이 조용히 사라진다. 운영자는 「올렸다」고만 알고,
 * 나중에 굿즈를 만들려 할 때 **화면용 128px 밖에 없다는 걸 그때 안다.**
 * 되살릴 방법은 없다(벡터화로 시도했으나 더 나빴다 — docs/build/28 §4-4).
 *
 * `null` 이면 안내할 것이 없다(원래 작았거나, 줄여도 인쇄에 충분하다).
 */
export function keepOriginalAdvice(
  from: { width: number; height: number },
  to: { width: number; height: number },
): { fromMm: number; toMm: number; text: string } | null {
  const before = printableSide(from.width, from.height);
  const after = printableSide(to.width, to.height);
  // 원본이 인쇄에 쓸 만했는데 줄인 결과가 못 미칠 때만 말한다
  if (before < PRINT_PX || after >= PRINT_PX) return null;
  const fromMm = printMm(before);
  const toMm = printMm(after);
  return {
    fromMm,
    toMm,
    text:
      `올리신 그림은 굿즈로 인쇄할 수 있는 크기예요(${Math.round(fromMm)}mm). ` +
      `화면용으로 줄이면 ${Math.round(toMm)}mm 가 되어 인쇄에는 못 써요. ` +
      `원본을 받아 두시면 나중에 키링·스티커로 만들 수 있어요.`,
  };
}
